import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RealtimeProvider } from './lib/realtime';
import { RouterProvider } from '@tanstack/react-router';
import { CardSkeleton, ErrorState, setPermissionLabeller } from '@micarahr/web-ui';
import { permissionLabel } from '@micarahr/permissions/web';
import './styles/globals.css';
import { router } from './app/router';
import { queryClient } from './lib/query';
import { MAGIC_LINK_PATH, SignInPage } from './features/auth/sign-in';
import { authService } from './services';
import { applySimulation, bootstrap } from './services/bootstrap';
import { useAuthStore, useSimulationStore, useWorkspaceStore } from './stores';

/*
 * How the person last moved — keyboard or pointer — on <html>, so the focus
 * ring shows for keyboard use only (styles/globals.css). Tab, arrows and
 * Enter count as keyboard; a modifier, or Escape closing a menu, does not.
 */
const NAVIGATION_KEYS = new Set([
  'Tab',
  'Enter',
  ' ',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);
document.addEventListener(
  'keydown',
  (event) => {
    if (NAVIGATION_KEYS.has(event.key)) document.documentElement.dataset.input = 'keyboard';
  },
  true,
);
document.addEventListener(
  'pointerdown',
  () => {
    document.documentElement.dataset.input = 'pointer';
  },
  true,
);

/**
 * Entry point.
 *
 * The demonstration data has to be in memory before any service call, so the
 * shell waits on bootstrap rather than letting forty components each discover
 * the store is empty.
 */

function Root() {
  const ready = useAuthStore((state) => state.ready);
  const setReady = useAuthStore((state) => state.setReady);
  const session = useAuthStore((state) => state.session);
  const setSession = useAuthStore((state) => state.setSession);
  const dataReady = useWorkspaceStore((state) => state.dataReady);
  const setDataReady = useWorkspaceStore((state) => state.setDataReady);

  const latencyMs = useSimulationStore((state) => state.latencyMs);
  const failAll = useSimulationStore((state) => state.failAll);
  const failingServices = useSimulationStore((state) => state.failingServices);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await bootstrap();
      if (cancelled) return;
      setDataReady(true, result.seeded, result.degraded);
      // Awaited because the API adapter has to present the refresh cookie to
      // the server; the mock's synchronous answer satisfies the same await.
      setSession(await authService.restore());
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [setDataReady, setReady, setSession]);

  // Simulation settings reach the services through the runtime, so no service
  // needs to know the Simulation Centre exists.
  useEffect(() => {
    applySimulation({ latencyMs, failAll, failingServices });
  }, [latencyMs, failAll, failingServices]);

  if (!ready || !dataReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
        <div className="w-full max-w-md space-y-3">
          <div className="flex items-baseline justify-center gap-1.5">
            <span className="text-2xl font-extrabold tracking-tight text-ink">Micara</span>
            <span className="text-2xl font-extrabold tracking-tight text-brand">HR</span>
          </div>
          <p className="text-center text-base text-ink-muted">Preparing your workspace…</p>
          <CardSkeleton lines={3} />
        </div>
      </div>
    );
  }

  if (!session) return <SignInPage />;

  /*
   * Sign-in is not a route — it renders wherever the URL happened to be when
   * the session was missing. Which means that after signing in *on*
   * `/sign-in`, the router woke up on a path it has no route for and answered
   * "Page not found" to somebody who did everything right. Landing on
   * `/sign-in` with a session is the one URL that never means itself, so it
   * is rewritten before the router mounts; every other URL is preserved,
   * because a person who signed in at /payroll wanted /payroll.
   */
  if (window.location.pathname === '/sign-in' || window.location.pathname === MAGIC_LINK_PATH) {
    // An emailed link opened in a tab that is already signed in has nothing
    // left to do: the session is here. (Its token stays unspent.)
    window.history.replaceState(null, '', '/');
  }

  return <RouterProvider router={router} />;
}

// Every "you do not have access" names the permission by the words the roles
// editor uses, never by its dotted key.
setPermissionLabeller(permissionLabel);

const container = document.getElementById('root');
if (!container) {
  throw new Error('MicaraHR could not start: #root is missing from index.html.');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/*
       * Inside the query provider, because the socket's only job is to
       * invalidate queries — an event is a hint that something changed, never
       * the change itself.
       */}
      <RealtimeProvider>
        <ErrorBoundaryRoot />
      </RealtimeProvider>
    </QueryClientProvider>
  </StrictMode>,
);

/** A last-resort boundary so a render error shows a state, not a white screen. */
function ErrorBoundaryRoot() {
  try {
    return <Root />;
  } catch (error) {
    return (
      <ErrorState
        title="MicaraHR could not start"
        message={error instanceof Error ? error.message : 'An unexpected error occurred.'}
        onRetry={() => window.location.reload()}
      />
    );
  }
}
